import React, { FC, useEffect, useState } from 'react';
import { Dirary } from '../../types/dirary';
import classes from './MultiInputMain.module.scss';
import { useRecoilState } from 'recoil';
import { User, WorkerWithDiary } from '../../types';
import { userState } from '../../states/userState';
import { formatDate } from '../../utils/functions/formatting';
import {
  createDefaultDiary,
  createDefaultDiaryForMulti,
  createNonProductionDiary,
  createNonProductionDiaryForMulti,
  nullADiaryList,
} from '../../utils/functions/createDefaultDiary';
import { useCheckSendable } from '../../hooks/singleInput/useCheckSendable';
import { calcWorkSchedule } from '../../utils/functions/workSchedule';
import { SHIFT_TYPE_NORMAL } from '../../utils/variables/shift';
import { useCheckDiaryList } from '../../hooks/singleInput/useCheckDiaryList';
import WorkersContainer from './workers/WorkersContainer';
import MultiInputHeader from './header/MultiInputHeader';
import MultiInputDetail from './detail/MultiInputDetail';
import { postIdCounterState } from '../../states/postIdCounterState';

//"HH:mm" → 通算分(夜勤2直対応: endMin初期値・計算用)
const timeToMin = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const MultiInputMain: FC = () => {
  const [user, setUser] = useRecoilState<User>(userState);

  const [workerList, setWorkerList] = useState<WorkerWithDiary[]>(
    user.initialParameters.workersForMultiInput
  );
  const { checkDiaryListSendable } = useCheckSendable();
  //読み込んだ日報リスト
  const [fetchDiaryList, setFetchDiaryList] = useState<Dirary[]>([]);
  //作成した日報リスト
  const [diaryList, setDiaryList] = useState<Dirary[]>([]);
  //追加・削除・更新日報リスト
  const { addDiaryList, deleteDiaryList, updateDiaryList } = useCheckDiaryList(
    diaryList,
    fetchDiaryList
  );
  //デフォルトの日報（一括）
  const [defaultDiary, setDefaultDiary] = useState<Dirary>(nullADiaryList);
  //デフォルトの製番（一括）
  const [defaultProductNo, setDefaultProductNo] = useState<string>(
    user.initialParameters.productNoForMultiInput!
  );
  //デフォルトの工場（一括）
  const [defaultFactoryNo, setDefaultFactoryNo] = useState<number>(
    user.initialParameters.factoryNoForMultiInput!
  );
  //デフォルトの工事区分（一括）
  // const [defaultConstruction, setDefaultCondruction] = useState<number>(
  //   user.initialParameters.constructionForMultiInput!
  // );
  const [defaultProductBno, setDefaultProductBno] = useState<string>(
    user.initialParameters.productBnoForSingleInput!
  );
  //デフォルトの伝票番号（一括）
  const [defaultSlipNumber, setDefaultSlipNumber] = useState<string>(
    user.initialParameters.slipNumberForMultiInput!
  );
  //新規発行IDカウンタ
  const [postIdCounter, setPostIdCounter] = useRecoilState(postIdCounterState);

  //作業開始日
  const [workingDate, setWorkingDate] = useState({
    obj: new Date(),
    text: formatDate(new Date()),
  });
  //社員情報
  const [workerInfo, setWorkerInfo] = useState<WorkerWithDiary[]>([]);
  //作業開始・終了時刻
  const [fromTime, setFromTime] = useState('08:30');
  const [toTime, setToTime] = useState('08:30');

  //合計時間
  const [totalTime, setTotalTime] = useState({
    hour: 0,
    min: 0,
    text: '00時間00分',
  });
  //送信可否
  const [isSendable, setIsSendable] = useState(false);
  //登録済有無
  const [isEmpty, setIsEmpty] = useState(false);
  //勤務区分(0=通常 / 1=夜勤)。日単位・全作業者共通。夜勤2直対応。
  const [shiftType, setShiftType] = useState<number>(SHIFT_TYPE_NORMAL);
  //当日終了の通算分(終了時刻・翌時刻併記表示用)
  const [endMin, setEndMin] = useState<number>(timeToMin('08:30'));
  //チェーンが24時間を超えているか(送信可否判定用・F5-1)
  const [isOverMaxChain, setIsOverMaxChain] = useState<boolean>(false);

  useEffect(() => {
    if (diaryList.length === 0 && isEmpty) {
      //brush-up 2026-06-17改訂: ラジオ再生成はクリーンな初期状態に揃える。
      //(夜勤ON→ラジオ削除→空 の後に作業者リストを変更したケースでも、ラジオ08:30・通常勤務へ
      // リセットし「夜勤のままラジオ」という矛盾状態を防ぐ。作業者変更＝完全な仕切り直し。)
      const nonProductionDiary = createNonProductionDiaryForMulti(
        workingDate.text,
        fromTime,
        toTime,
        totalTime,
        defaultFactoryNo,
        SHIFT_TYPE_NORMAL //再生成時は通常へリセット
      );
      const newDiary = { ...nonProductionDiary, id: postIdCounter };
      setPostIdCounter((preCounter) => preCounter - 1);
      setDiaryList([newDiary]);
      setFromTime('08:30');
      setShiftType(SHIFT_TYPE_NORMAL);
    }
  }, [workerList, isEmpty]);
  useEffect(() => {
    //デフォルトをセット
    setDefaultDiary(
      // createDefaultDiary(
      //   workerInfo,
      //   workingDate.text,
      //   fromTime,
      //   toTime,
      //   totalTime,
      //   // hasBreak,
      //   defaultProductNo,
      //   defaultProductBno,
      //   defaultFactoryNo,
      //   // defaultConstruction,
      //   defaultSlipNumber
      // )
      createDefaultDiaryForMulti(
        workingDate.text,
        fromTime,
        toTime,
        totalTime,
        '', //productNo
        '', //productBno
        defaultFactoryNo,
        '0000000', //slipNumber
        shiftType //夜勤2直対応: 現在のトグル状態を反映
      )
    );
  }, [
    workerInfo,
    fromTime,
    toTime,
    totalTime,
    // hasBreak,
    workingDate,
    defaultProductNo,
    defaultProductBno,
    defaultFactoryNo,
    // defaultConstruction,
    defaultSlipNumber,
    shiftType,
  ]);

  useEffect(() => {
    setDefaultProductNo(user.initialParameters.productNoForMultiInput!);
    setDefaultProductBno(user.initialParameters.productBnoForMultiInput!);
  }, [
    user.initialParameters.productBnoForMultiInput,
    user.initialParameters.productNoForMultiInput,
  ]);
  useEffect(() => {
    setDefaultFactoryNo(user.initialParameters.factoryNoForMultiInput!);
    // setDefaultCondruction(user.initialParameters.constructionForMultiInput!);
  }, [
    user.initialParameters.factoryNoForMultiInput,
    // user.initialParameters.constructionForMultiInput,
  ]);
  useEffect(() => {
    setDefaultSlipNumber(user.initialParameters.slipNumberForMultiInput!);
  }, [user.initialParameters.slipNumberForMultiInput]);
  useEffect(() => {
    const res = checkDiaryListSendable(
      totalTime,
      diaryList,
      deleteDiaryList,
      isOverMaxChain //夜勤2直対応: 24h超えで送信不可(コード3)
    );
    if (res === 0 && isEmpty) {
      setIsSendable(true);
    } else {
      setIsSendable(false);
    }
    if (diaryList.length === 0) {
      setTotalTime({
        hour: 0.17,
        min: 10,
        text: '00時間00分',
      });

      // setFromTime("08:30");
      setToTime(fromTime);
      setEndMin(timeToMin(fromTime));
      setIsOverMaxChain(false);
    } else {
      //夜勤2直対応: 新エンジンで合計・終了時刻・24h超えを算出
      const schedule = calcWorkSchedule(
        fromTime,
        diaryList.map((d) => ({
          minutes: d.workingTimeByMinute,
          isBreak: d.isBreak,
        })),
        shiftType
      );
      setTotalTime({
        hour: schedule.totalHour,
        min: schedule.totalMin,
        text: schedule.totalText,
      });
      setToTime(schedule.endClock);
      setEndMin(schedule.endMin);
      setIsOverMaxChain(schedule.isOverMaxChain);
    }
  }, [
    diaryList,
    isEmpty,
    totalTime.text,
    deleteDiaryList,
    fromTime,
    shiftType,
    isOverMaxChain,
  ]);

  //開始時刻・勤務区分が変更されたとき
  useEffect(() => {
    //夜勤2直対応: fromTime/shiftType 変更時にチェーン全体を即時再計算(§4.4)
    const schedule = calcWorkSchedule(
      fromTime,
      diaryList.map((d) => ({
        minutes: d.workingTimeByMinute,
        isBreak: d.isBreak,
      })),
      shiftType
    );
    const finalList = diaryList.map((diary: Dirary, i) => {
      return {
        ...diary,
        startTime: schedule.entries[i].startClock,
        endTime: schedule.entries[i].endClock,
        startTimeText: schedule.entries[i].startText,
        endTimeText: schedule.entries[i].endText,
        spansNextDay: schedule.entries[i].spansNextDay,
        shiftType: shiftType,
        totalWorkingTimeByHour: schedule.totalHour,
        totalWorkingTimeByMinute: schedule.totalMin,
      };
    });
    setDiaryList(finalList);
  }, [fromTime, shiftType]);
  return (
    <div className={classes.rootContainer}>
      <div>
        <MultiInputHeader
          setTotalTime={setTotalTime}
          totalTime={totalTime}
          workingDate={workingDate}
          setWorkingDate={setWorkingDate}
          fromTime={fromTime}
          toTime={toTime}
          setFromTime={setFromTime}
          setToTime={setToTime}
          setFetchDiaryList={setFetchDiaryList}
          diaryList={diaryList}
          setDiaryList={setDiaryList}
          shiftType={shiftType}
          setShiftType={setShiftType}
          endMin={endMin}
        />
        <WorkersContainer
          workingDate={workingDate}
          setWorkerList={setWorkerList}
          workerList={workerList}
          isSendable={isEmpty}
          setIsSendable={setIsEmpty}
        />
      </div>
      <div className={classes.hr} />
      <MultiInputDetail
        defaultDiary={defaultDiary}
        diaryList={diaryList}
        setDiaryList={setDiaryList}
        totalTime={totalTime}
        setTotalTime={setTotalTime}
        isSendable={isSendable}
        setWorkerList={setWorkerList}
        workerList={workerList}
        fromTime={fromTime}
        shiftType={shiftType}
        isOverMaxChain={isOverMaxChain}
        postIdCounter={postIdCounter}
        setPostIdCounter={setPostIdCounter}
      />
    </div>
  );
};

export default MultiInputMain;
